import DebugLocationPopup from "@components/Debug/LocationPopup";
import SystemError from "@components/SystemError";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import PulseLoader from "@hudson/ui/components/Loader/PulseLoader";
import { RegisterOptions, useFormContext } from "@hudson/ui/components/ReactHookForm";
import { useAdjustFrameForPopup } from "@hudson/ui/lib/use_autosize";
import normalStyles from "@hudson/ui/styles/Form.module.css";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import { findLocations as defaultFindLocations } from "@services/find_locations/index";
import isValidLocation from "@services/is_valid_location";
import useDebounce from "@utils/use_debounce";
import chroma from "chroma-js";
import { useCombobox } from "downshift";
import dynamic from "next/dynamic";
import React, { useEffect, useRef, useState } from "react";
import ClearButton from "./internal/ClearButton";
import FixedLocationDialog from "./internal/FixedLocationDialog";
import defaultLocationPredictionSelected from "./internal/location_prediction_selected";
import NearbyLocationsDialog from "./internal/NearbyLocationsDialog";
import NotFoundMessage from "./internal/NotFoundMessage";
import { Place } from "./internal/Place";
import useFixedLocationsPlaceList from "./internal/use_fixed_locations";
import styles from "./Location.module.css";
import TypeIcon from "./TypeIcon";

const POWERED_BY_GOOGLE_LOGO =
  "data:image/png;base64,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";

interface LocationProps {
  className?: string;
  configLoading?: boolean;
  disabled?: boolean;
  fieldName: string;
  fieldValidations?: RegisterOptions;
  findLocations?: be.FindLocationsFunction;
  focusOnMount?: boolean;
  keepValueOnUnregister?: boolean;
  hidePuds?: boolean;
  limitLocations?: number;
  locationPredictionRenderer?: React.ComponentType<be.LocationPrediction>;
  locationPredictionSelected?: be.LocationPredictionSelectedFunction;
  noAutosizing?: boolean;
  normalFieldStyle?: boolean;
  onLocationChange?: () => void;
  placeholder?: string;
  required?: boolean;
  searchCenter?: be.LatLng;
  serviceAreaFilter?: number | number[];
}

function truncateWithEllipses(text: string, max: number): string {
  return text.substr(0, max - 1) + (text.length > max ? "..." : "");
}

function Location({
  className,
  configLoading,
  disabled,
  fieldName,
  fieldValidations = {},
  findLocations = defaultFindLocations,
  focusOnMount,
  hidePuds,
  keepValueOnUnregister = true,
  limitLocations,
  locationPredictionRenderer = Place,
  locationPredictionSelected = defaultLocationPredictionSelected,
  noAutosizing,
  normalFieldStyle,
  onLocationChange,
  placeholder,
  required = true,
  searchCenter,
  serviceAreaFilter,
}: LocationProps): JSX.Element | null {
  let fixedLocationList = useFixedLocationsPlaceList(serviceAreaFilter, hidePuds);
  const { loading, data, error } = useConfig(["defaultSearchCenter { lat lng }"]);
  const [showSystemError, setShowSystemError] = useState(false);
  const {
    data: cData,
    loading: cLoading,
    error: cError,
  } = useContent([
    "beErrLocTooGeneral",
    "beErrNoFareKey",
    "beErrNoLocsFound",
    "beErrFieldRequired",
  ]);

  // Focus input field on mount
  useEffect(() => {
    if (focusOnMount) {
      searchFieldRef.current?.focus();
      setResultsOpen(!!fixedLocationList.length);
    }
  }, [fixedLocationList]);

  // Hook input into form
  const { formState, getFieldState, clearErrors, watch, register, setError, setValue, unregister } =
    useFormContext();
  useEffect(() => {
    register(fieldName, {
      ...fieldValidations,
      validate: {
        required: (location) => !!location || !required || cData?.content.beErrFieldRequired,
        locationError: (location) => !location?.error || location?.error,
        badFareKeyLookup: (location) => !!location?.code || cData?.content.beErrNoFareKey,
        validLocation: async (location) =>
          (await isValidLocation(location)) || cData?.content.beErrLocTooGeneral,
      },
    });
    return () => {
      unregister(fieldName, { keepValue: keepValueOnUnregister });
    };
  }, [cData, register, unregister]);
  const value = watch(fieldName);
  const onChange = async (location?: be.Location) => {
    setResults([]);
    (document.activeElement as HTMLElement)?.blur();
    await setValue(fieldName, location, { shouldDirty: true, shouldValidate: true });
    if (onLocationChange) onLocationChange();
  };
  const fieldError = getFieldState(fieldName)?.error;
  const [locLoading, setLocLoading] = useState<boolean>(false);
  const [results, setResults] = useState<be.LocationPrediction[] | ["noResults"]>([]);
  const [resultsLoading, setResultsLoading] = useState<boolean>(false);
  const [resultsOpen, setResultsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(value?.displayName);
  const [showFixed, setShowFixed] = useState<[string, string, number | undefined] | undefined>();

  useAdjustFrameForPopup({
    enabled: !noAutosizing,
    isShown: resultsOpen,
    popupQuery: `[data-location-results="${fieldName}"]`,
    extraHeight: 10,
    useInterval: true,
    label: "locations",
  });

  // Needed to keep field from gainig focus after nearby locs
  const [loseFocus, setLoseFocus] = useState<boolean>(false);
  const [showNearby, setShowNearby] = useState<
    [be.Location, Array<any>, be.Location | undefined] | undefined
  >();
  const searchTermRef = useRef<string | undefined>();
  const searchFieldRef = useRef<HTMLInputElement | null>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Ensure we have access to the most current search term from within useEffect.
  searchTermRef.current = searchTerm;

  // If value changes, update field value
  useEffect(() => setSearchTerm(value?.displayName || ""), [value?.displayName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Respond to search term (minimum 2 characters to trigger search)
  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length >= 2 && debouncedSearchTerm != value?.displayName) {
      setResults([]);
      loadLocations({ limitLocations }).then(function (predictions) {
        setResults(predictions.length == 0 ? ["noResults"] : predictions);
        if (debouncedSearchTerm == searchTermRef.current) setResultsLoading(false);
      });
    }
  }, [debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadLocations(options: be.FindLocationsFunctionOptions = {}) {
    return findLocations({
      searchTerm: debouncedSearchTerm,
      searchCenter: searchCenter || data?.config.defaultSearchCenter,
      searchRadius: 30000,
      serviceArea: serviceAreaFilter,
      options,
    });
  }

  function onResultSelect(
    _event: MouseEvent | undefined,
    { defaultBehavior, result }: { defaultBehavior: boolean; result: be.LocationPrediction },
  ): void {
    const fn = defaultBehavior ? defaultLocationPredictionSelected : locationPredictionSelected;
    setResultsOpen(false);
    fn({
      prediction: result,
      serviceAreaFilter: serviceAreaFilter,
      fns: {
        clearErrors: () => clearErrors(fieldName),
        clearResults: () => setResults([]),
        runDefaultBehavior: (prediction) =>
          onResultSelect(undefined, { result: prediction, defaultBehavior: true }),
        keepDialogOpen: () => setResultsOpen(true),
        loadLocations: (options) => loadLocations({ limitLocations, ...options }),
        updateLocation: (location) => onChange(location),
        updateLocationLoadingStatus: (on) => setLocLoading(on),
        showFixedLocationPopup: (type, typeString, serviceArea) => {
          document.activeElement?.blur();
          setLoseFocus(true);
          setShowFixed([type, typeString, serviceArea]);
        },
        showLoadingAnimation: (on) => setResultsLoading(on),
        showNearbyLocationsPopup: (
          anchorPoint: be.Location,
          locations: be.Location[],
          requestedLocation?: be.Location,
        ) => {
          document.activeElement?.blur();
          setLoseFocus(true);
          setShowNearby([anchorPoint, locations, requestedLocation]);
        },
        showResults: (predictions) => setResults(predictions),
        showSystemError: () => setShowSystemError(true),
        updateSearchTerm: (term: string) => setSearchTerm(term),
      },
    });
  }

  function onInputValueChange(inputValue: string): void {
    setResultsOpen(true);
    if (!inputValue) setResults([]);
    setResultsLoading(true);
    setSearchTerm(inputValue);
  }

  // Compute the items to show in the dropdown
  const displayItems =
    results.length === 1 && results[0] === "noResults"
      ? []
      : fixedLocationList.length
        ? [...(results as be.LocationPrediction[]), ...(results.length ? [] : fixedLocationList)]
        : (results as be.LocationPrediction[]);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
  } = useCombobox({
    items: displayItems,
    inputValue: searchTerm ?? "",
    isOpen: resultsOpen,
    onInputValueChange: ({ inputValue, type }) => {
      // Only trigger search on user input changes, not programmatic ones
      if (type === useCombobox.stateChangeTypes.InputChange) {
        onInputValueChange(inputValue ?? "");
      }
    },
    onSelectedItemChange: ({ selectedItem, type }) => {
      // Only handle keyboard selection (Enter key), clicks are handled by onClick
      if (
        selectedItem &&
        type === useCombobox.stateChangeTypes.ItemClick === false
      ) {
        onResultSelect(undefined, {
          defaultBehavior: false,
          result: selectedItem,
        });
      }
    },
    itemToString: (item) => item?.title ?? "",
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          // Prevent closing on blur if we're selecting an item
          return {
            ...changes,
            isOpen: false,
          };
        default:
          return changes;
      }
    },
  });

  let addressDisplay;
  if (value?.displayAddress)
    addressDisplay = (
      <div
        className={`pl-[4rem] pr-[3.5rem] width-[100%]  text-gray-600  font-thin text-sm truncate ${
          locLoading ? "invisible" : "visible"
        }`}
      >
        {truncateWithEllipses(value.displayAddress, 60)}
      </div>
    );

  return error || cError ? null : (
    <>
      {showSystemError && <SystemError onHide={() => setShowSystemError(false)} />}
      <div className="relative flex-1 min-w-0">
        {showFixed && (
          <FixedLocationDialog
            show={!!showFixed}
            locationType={showFixed[0]}
            locationTypeString={showFixed[1]}
            onHide={() => {
              setShowFixed(undefined);
              setResultsOpen(false);
            }}
            onLocationSelect={function (loc, locationType) {
              onResultSelect(undefined, {
                result: {
                  source: "hudson",
                  title: loc.displayName,
                  description: loc.displayAddress,
                  // Prefer direct location when selecting from the fixed dialog
                  location: loc,
                  partiallocation: locationType == "PUD" ? loc : undefined,
                  key: loc.code,
                  servicearea: loc.serviceAreas,
                  type: loc.typeString,
                },
              });
            }}
            serviceArea={showFixed[2]}
            noAutosizing={noAutosizing}
          />
        )}
        {showNearby && (
          <NearbyLocationsDialog
            show={!!showNearby}
            anchorPoint={showNearby[0]}
            locations={showNearby[1]}
            requestedLocation={showNearby[2]}
            onHide={() => {
              onChange();
              setShowNearby(undefined);
              setResultsOpen(false);
            }}
            onLocationSelect={function (loc) {
              onResultSelect(undefined, {
                result: {
                  source: "hudson",
                  title: loc.displayName,
                  description: loc.displayAddress,
                  location: loc,
                  key: loc.code,
                  servicearea: loc.serviceAreas,
                  type: loc.typeString,
                },
              });
            }}
          />
        )}
        {/* shadow-sm block text-base border-gray-400 pt-[0.75rem] pb-[0.5rem] */}
        <div
          className={`${normalFieldStyle ? `${normalStyles.input} ${styles.normalContainer}` : `${styles.container} flex-1 self-start max-w-full`} relative ${className}`}
        >
          <DebugLocationPopup
            label="Location details"
            location={value}
            className="absolute mb-4 text-sm font-bold -top-5"
          />

          <TypeIcon
            className={`absolute h-8 ${value?.displayAddress ? "top-5" : "top-2"} left-5 z-10`}
            type={value?.typeString}
            typeKey={value?.type}
          />

          {/* Custom Downshift Combobox */}
          <div className={`${normalFieldStyle ? styles.normalSearch : styles.search} text-xl relative`}>
            <input
              {...getInputProps({
                ref: searchFieldRef,
                disabled: disabled || loading || cLoading || configLoading,
                placeholder: loading || cLoading || configLoading ? "..." : placeholder,
                onBlur: () => {
                  setSearchTerm(value?.displayName?.toString() || "");
                  setResultsOpen(false);
                },
                onFocus: () => {
                  if (loseFocus) {
                    document.activeElement?.blur();
                    setResultsOpen(false);
                    setLoseFocus(false);
                  } else {
                    setResults([]);
                    setResultsOpen(!!fixedLocationList.length);
                  }
                },
              })}
              className={styles.input}
            />
            <div
              className={`${styles.resultsContainer} ${isOpen ? styles.resultsVisible : ""}`}
              data-location-results={fieldName}
            >
              <ul
                {...getMenuProps()}
                className={styles.results}
              >
                {isOpen && (
                  <>
                    {displayItems.length === 0 ? (
                      <li className={styles.noResults}>
                        {resultsLoading ? (
                          <ConcentricLoader />
                        ) : (
                          <NotFoundMessage message={cData?.content.beErrNoLocsFound} />
                        )}
                      </li>
                    ) : (
                      displayItems.map((item, index) => {
                        const ResultRenderer = locationPredictionRenderer;
                        const { key: _key, ...itemProps } = item;
                        return (
                          <li
                            key={item.key ?? index}
                            {...getItemProps({
                              item,
                              index,
                              onClick: () => {
                                onResultSelect(undefined, {
                                  defaultBehavior: false,
                                  result: item,
                                });
                              },
                            })}
                            className={`${styles.result} ${highlightedIndex === index ? styles.resultHighlighted : ""}`}
                            data-source={item.source}
                            data-final={item.final}
                            data-first={index === 0 ? "true" : undefined}
                          >
                            <ResultRenderer {...itemProps} />
                          </li>
                        );
                      })
                    )}
                  </>
                )}
              </ul>
              {isOpen && displayItems.some((item) => item.source === "google" || item.source === "universalGoogle") && (
                <div className={styles.googleAttribution}>
                  <img src={POWERED_BY_GOOGLE_LOGO} alt="Powered by Google" />
                </div>
              )}
            </div>
          </div>

          {addressDisplay}

          {fieldError && (
            <div
              style={{ backgroundColor: chroma("red").set("hsl.s", 0.63).set("hsl.l", 0.56).hex() }}
              className={`rounded-b-md relative -bottom-2 w-full inline-flex items-center px-2.5 py-1 pt-1.5 text-sm font-medium text-white`}
            >
              {fieldError.message}
            </div>
          )}

          {locLoading ? (
            <PulseLoader
              className={`absolute right-5 ${value?.displayAddress ? "top-4" : "top-2"}`}
              colorVar="primary-color"
            />
          ) : disabled || configLoading ? null : (
            <ClearButton
              className={`absolute right-1 ${value?.displayAddress ? "top-2" : "top-0"}`}
              visible={!!searchTerm}
              onClick={function () {
                setSearchTerm("");
                setResults([]);
                onChange();
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default dynamic(() => Promise.resolve(Location), {
  ssr: false,
});
